import React, { useState, useEffect, useRef } from "react";
import "./App.css";
import {
  CheckCircle,
  XCircle,
  Lock,
  ArrowRight,
  MessageCircle,
  Lightbulb,
} from "lucide-react";

// ─────────────────────────────────────────
// CONFIG — Remplacez avec votre lien Calendly
// ─────────────────────────────────────────
const CALENDLY_URL = "https://calendly.com/noctivue/appel-decouverte-liberation-relation-toxique";

// ─────────────────────────────────────────
// Hook scroll animation (fade-up)
// ─────────────────────────────────────────
const useFadeUp = () => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.08 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return [ref, isVisible];
};

const FadeUp = ({ children, className = "", delay = 0 }) => {
  const [ref, isVisible] = useFadeUp();
  return (
    <div
      ref={ref}
      className={`fade-up ${isVisible ? "visible" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

// ─────────────────────────────────────────
// Bouton CTA réutilisable
// ─────────────────────────────────────────
const CTAButton = ({ className = "", size = "normal", dark = true, label = "Réserver mon appel découverte" }) => (
  <a
    href={CALENDLY_URL}
    target="_blank"
    rel="noopener noreferrer"
    data-testid="cta-calendly-button"
    className={`
      cta-btn inline-flex items-center gap-3 font-sans font-semibold tracking-wide rounded-full
      transition-all duration-300
      ${dark
        ? "bg-[#1E293B] text-white hover:bg-[#0F172A] hover:shadow-lg hover:-translate-y-0.5"
        : "bg-white text-[#1E293B] hover:bg-[#F5F2EA] hover:shadow-xl hover:-translate-y-1"
      }
      ${size === "large" ? "px-10 py-5 text-base" : "px-7 py-3.5 text-sm"}
      ${className}
    `}
  >
    {label}
    <ArrowRight size={17} strokeWidth={2} />
  </a>
);

// ─────────────────────────────────────────
// HEADER
// ─────────────────────────────────────────
const Header = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${
        scrolled
          ? "bg-[#FDFBF7]/96 backdrop-blur-sm shadow-sm py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <div
          className="font-serif text-[#2A2724] text-xl tracking-[0.25em] uppercase"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          Noctivue
        </div>
        <CTAButton className="hidden md:inline-flex" />
      </div>
    </header>
  );
};

// ─────────────────────────────────────────
// SECTION 1 — HERO (Accroche)
// ─────────────────────────────────────────
const HeroSection = () => (
  <section
    data-testid="hero-section"
    className="relative min-h-screen flex items-center justify-center overflow-hidden"
  >
    {/* Background */}
    <div
      className="absolute inset-0 bg-cover bg-center bg-no-repeat"
      style={{
        backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/625f59e0-0a35-409d-822e-917cb1e67548/images/80aed8ddfeadc76fd3511aaf0dd96b320378f4d569309965d0e7faf705ec7a58.png')`,
      }}
    />
    {/* Overlay doux */}
    <div className="absolute inset-0 bg-gradient-to-b from-[#FDFBF7]/75 via-[#FDFBF7]/65 to-[#FDFBF7]/95" />

    {/* Contenu */}
    <div className="relative z-10 max-w-3xl mx-auto px-6 text-center pt-24 pb-16">
      <p className="hero-overline font-sans uppercase tracking-[0.3em] text-xs text-[#57534E] mb-8">
        Noctivue — Bruno Carvalho
      </p>

      <h1
        className="hero-title font-serif text-5xl sm:text-6xl lg:text-7xl text-[#2A2724] leading-[1.1] mb-6"
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        Tu n'es peut-être pas
        <br />
        simplement fatiguée.
      </h1>

      <p
        className="hero-subtitle font-serif text-2xl sm:text-3xl text-[#57534E] italic leading-relaxed mb-12"
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        Tu vis peut-être en mode survie
        <br />
        depuis trop longtemps.
      </p>

      <p
        className="hero-subtitle font-sans text-base sm:text-lg text-[#57534E] leading-relaxed mt-6 mb-10"
      >
        Tu dis souvent : « Je vais gérer. » Tu culpabilises quand tu te reposes. Tu t'endors épuisée. Tu te réveilles déjà fatiguée.
      </p>

      <div className="flex flex-col md:flex-row items-center gap-6 md:gap-10 mb-10 max-w-2xl mx-auto">
        <div className="w-[65%] sm:w-[280px] md:w-[320px] flex-shrink-0">
          <div className="overflow-hidden rounded-2xl shadow-sm">
            <img
              src="/bruno.jpg"
              alt="Bruno Carvalho — Noctivue"
              className="w-full h-auto object-cover"
              data-testid="hero-coach-photo"
            />
          </div>
        </div>
        <div className="text-center md:text-left">
          <p className="font-sans text-sm sm:text-base text-[#57534E] mb-5">
            Si tu te reconnais… cet appel est pour toi.
          </p>
          <CTAButton size="large" />
        </div>
      </div>
    </div>

    {/* Indicateur scroll */}
    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-center scroll-hint">
      <div className="scroll-line w-px h-10 bg-[#2A2724]/25 mx-auto mb-3" />
      <p className="font-sans text-[10px] text-[#57534E]/50 tracking-[0.25em] uppercase">
        Défiler
      </p>
    </div>
  </section>
);

// ─────────────────────────────────────────
// SECTION 2 — IDENTIFICATION / EMPATHIE
// ─────────────────────────────────────────
const IdentificationSection = () => {
  const statements = [
    "Tu dis toujours « je vais gérer » — même quand tu n'en peux plus.",
    "Tu réfléchis à tout, tout le temps, sans jamais décrocher.",
    "Tu culpabilises dès que tu te reposes.",
    "Même dans les moments calmes, tu restes en alerte.",
    "Tu ne sais plus ce que ça fait de vraiment souffler.",
  ];

  return (
    <section
      data-testid="identification-section"
      className="py-24 lg:py-32 bg-[#FDFBF7]"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Image */}
          <FadeUp className="relative">
            <div className="relative overflow-hidden rounded-3xl aspect-[4/5] shadow-sm">
              <img
                src="https://images.unsplash.com/photo-1761389294347-4e7b17731a5b?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800"
                alt="Femme pensive, regard vers la lumière"
                className="w-full h-full object-cover scale-105 img-hover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#FDFBF7]/20 to-transparent" />
            </div>
          </FadeUp>

          {/* Contenu */}
          <div className="space-y-8">
            <FadeUp delay={100}>
              <p className="font-sans uppercase tracking-[0.25em] text-xs text-[#57534E]">
                Est-ce que ça te parle ?
              </p>
            </FadeUp>

            <FadeUp delay={180}>
              <h2
                className="font-serif text-4xl sm:text-5xl text-[#2A2724] leading-tight"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Et au fond de toi…
                <br />
                <em>
                  tu sais que cette fatigue
                  <br />
                  n'est pas normale.
                </em>
              </h2>
            </FadeUp>

            <div className="space-y-1 pt-2">
              {statements.map((statement, i) => (
                <FadeUp key={i} delay={280 + i * 70}>
                  <div className="flex items-start gap-4 py-3.5 border-b border-[#2A2724]/8 group">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#1E293B] mt-[10px] flex-shrink-0 group-hover:scale-150 transition-transform duration-200" />
                    <p className="font-sans text-base text-[#57534E] leading-relaxed">
                      {statement}
                    </p>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────
// SECTION 3 — LE PROBLÈME
// ─────────────────────────────────────────
const ProblemeSection = () => (
  <section
    data-testid="probleme-section"
    className="py-24 lg:py-32 bg-[#F5F2EA]"
  >
    <div className="max-w-3xl mx-auto px-6 text-center">
      <FadeUp>
        <p className="font-sans uppercase tracking-[0.25em] text-xs text-[#57534E] mb-10">
          Ce que personne ne t'explique
        </p>
      </FadeUp>

      <FadeUp delay={100}>
        <h2
          className="font-serif text-4xl sm:text-5xl text-[#2A2724] leading-tight mb-8"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          Le problème, ce n'est pas toi.
          <br />
          <em>Ton système nerveux est resté bloqué en état d'alerte.</em>
        </h2>
      </FadeUp>

      <FadeUp delay={200}>
        <p className="font-sans text-base text-[#57534E] leading-relaxed mb-6">
          C'est pour ça que tu continues à vivre comme si tu devais toujours te protéger.
        </p>
      </FadeUp>

      <FadeUp delay={300}>
        <p
          className="font-serif text-xl sm:text-2xl text-[#2A2724] italic leading-relaxed"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          Même quand il n'y a plus de danger.
        </p>
      </FadeUp>
    </div>
  </section>
);

// ─────────────────────────────────────────
// SECTION — COUP DE RÉALITÉ
// ─────────────────────────────────────────
const UrgenceSection = () => (
  <section data-testid="urgence-section" className="py-24 lg:py-28 bg-[#FDFBF7]">
    <div className="max-w-2xl mx-auto px-6 text-center">
      <FadeUp>
        <h2
          className="font-serif text-3xl sm:text-4xl text-[#2A2724] leading-tight mb-10"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          Et tant que tu restes en mode survie…
        </h2>
      </FadeUp>

      <div className="space-y-4 mb-8">
        {[
          "tu continues de tout porter, même ce qui ne t'appartient pas",
          "la fatigue s'accumule, jour après jour",
          "et tu ne retrouves jamais ce sentiment de calme intérieur",
        ].map((item, i) => (
          <FadeUp key={i} delay={120 + i * 90}>
            <p className="font-sans text-base text-[#57534E] leading-relaxed py-3 border-b border-[#2A2724]/8">
              {item}
            </p>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

// ─────────────────────────────────────────
// SECTION 4 — LA SOLUTION
// ─────────────────────────────────────────
const SolutionSection = () => (
  <section
    data-testid="solution-section"
    className="py-24 lg:py-32 bg-[#FDFBF7] relative overflow-hidden"
  >
    <div
      className="absolute inset-0 opacity-8 bg-cover bg-center pointer-events-none"
      style={{
        backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/625f59e0-0a35-409d-822e-917cb1e67548/images/51bbf7fa35607c51005451794aba97c006329141219b140874b0bfcd1df91dca.png')`,
        opacity: 0.06,
      }}
    />
    <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
      <FadeUp>
        <p className="font-sans uppercase tracking-[0.25em] text-xs text-[#57534E] mb-10">
          L'approche Noctivue
        </p>
      </FadeUp>

      <FadeUp delay={100}>
        <h2
          className="font-serif text-4xl sm:text-5xl text-[#2A2724] leading-tight mb-6"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          Je t'aide à sortir du mode survie
          <br />
          pour que ton corps <em>réapprenne la sécurité</em>,
          <br />
          et que tu retrouves enfin
          <br />
          un vrai calme intérieur.
        </h2>
      </FadeUp>

      <div className="grid sm:grid-cols-3 gap-6 mt-16">
        {[
          {
            title: "Parler ne suffit pas",
            desc: "Tu peux comprendre ce qui se passe. Mais ton corps, lui, reste en alerte.",
            highlight: false,
          },
          {
            title: "Se forcer à avancer non plus",
            desc: "Tu peux te dire « ça va aller ». Mais le mode survie, lui, ne s'arrête pas comme ça.",
            highlight: false,
          },
          {
            title: "Ton système nerveux doit retrouver un sentiment de sécurité",
            desc: "C'est là que le vrai changement commence.",
            highlight: true,
          },
        ].map((item, i) => (
          <FadeUp key={i} delay={220 + i * 110}>
            <div
              className={`rounded-3xl p-8 h-full flex flex-col transition-all duration-300 hover:-translate-y-1 ${
                item.highlight
                  ? "bg-[#1E293B] shadow-lg"
                  : "bg-[#F5F2EA] border border-[#2A2724]/6"
              }`}
            >
              <p
                className={`font-serif text-xl leading-snug mb-4 ${
                  item.highlight ? "text-white" : "text-[#2A2724]"
                }`}
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                {item.title}
              </p>
              <p
                className={`font-sans text-sm leading-relaxed ${
                  item.highlight ? "text-[#D1C7BD]" : "text-[#57534E]"
                }`}
              >
                {item.desc}
              </p>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

// ─────────────────────────────────────────
// SECTION — PROJECTION POSITIVE
// ─────────────────────────────────────────
const ProjectionSection = () => {
  const items = [
    "Un corps qui arrête enfin de fonctionner en alerte",
    "Un sommeil qui devient vraiment réparateur",
    "Un mental qui ralentit, naturellement",
    "La capacité de te poser sans culpabiliser",
    "Et surtout… le sentiment de redevenir toi-même",
  ];

  return (
    <section data-testid="projection-section" className="py-24 lg:py-28 bg-[#F5F2EA]">
      <div className="max-w-2xl mx-auto px-6 text-center">
        <FadeUp>
          <p className="font-sans uppercase tracking-[0.25em] text-xs text-[#57534E] mb-6">
            Ce qui t'attend
          </p>
        </FadeUp>

        <FadeUp delay={100}>
          <h2
            className="font-serif text-4xl sm:text-5xl text-[#2A2724] leading-tight mb-12"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Ce que tu peux <em>retrouver</em>
          </h2>
        </FadeUp>

        <div className="space-y-3 text-left">
          {items.map((item, i) => (
            <FadeUp key={i} delay={200 + i * 80}>
              <div className="flex items-center gap-4 bg-white/60 rounded-2xl px-6 py-4 border border-white/80">
                <CheckCircle size={18} strokeWidth={1.5} className="text-[#1E293B] flex-shrink-0" />
                <p className="font-sans text-base text-[#2A2724]">{item}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────
// SECTION — DIFFÉRENCIATION
// ─────────────────────────────────────────
const DifferencationSection = () => (
  <section data-testid="differenciation-section" className="py-24 lg:py-28 bg-[#F5F2EA]">
    <div className="max-w-2xl mx-auto px-6 text-center">
      <FadeUp>
        <p className="font-sans uppercase tracking-[0.25em] text-xs text-[#57534E] mb-6">
          Ce qui change tout
        </p>
      </FadeUp>

      <FadeUp delay={100}>
        <h2
          className="font-serif text-4xl sm:text-5xl text-[#2A2724] leading-tight mb-12"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          Pourquoi cet accompagnement
          <br />
          <em>est différent</em>
        </h2>
      </FadeUp>

      <div className="space-y-4 text-left">
        {[
          "Tu n'as pas besoin d'être plus forte. Tu as besoin que ton corps retrouve enfin un sentiment de sécurité.",
          "Ton système nerveux est resté bloqué. Tant qu'il ne sort pas de cet état, le mental seul ne suffit pas.",
          "C'est exactement ce que l'on fait ensemble. Pas dans le mental. Dans le corps.",
        ].map((item, i) => (
          <FadeUp key={i} delay={200 + i * 100}>
            <div className="bg-white/60 border border-white/80 rounded-2xl px-6 py-5">
              <p className="font-sans text-base text-[#2A2724] leading-relaxed">
                {item}
              </p>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

// ─────────────────────────────────────────
// SECTION 5 — L'APPEL DÉCOUVERTE (mid CTA)
// ─────────────────────────────────────────
const AppelDecouverteSection = () => {
  const benefits = [
    "comprendre pourquoi tu restes bloquée en mode survie",
    "identifier ce qui empêche ton corps de relâcher",
    "voir clairement ce qui te maintient dans cet état",
    "découvrir comment en sortir, concrètement",
  ];

  return (
    <section
      data-testid="appel-decouverte-section"
      className="py-24 lg:py-32 bg-[#E3E8E3]"
    >
      <div className="max-w-2xl mx-auto px-6 text-center">
        <FadeUp>
          <p className="font-sans uppercase tracking-[0.25em] text-xs text-[#57534E] mb-6">
            L'étape suivante
          </p>
        </FadeUp>

        <FadeUp delay={100}>
          <h2
            className="font-serif text-4xl sm:text-5xl text-[#2A2724] leading-tight mb-6"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Cet appel découverte{" "}
            <em className="text-[#1E293B]">30 min</em>
            <br />
            va te permettre de :
          </h2>
        </FadeUp>

        <div className="space-y-3 mb-10 text-left mt-10">
          {benefits.map((benefit, i) => (
            <FadeUp key={i} delay={220 + i * 80}>
              <div className="flex items-center gap-4 bg-white/55 rounded-2xl px-6 py-4 border border-white/80">
                <CheckCircle
                  size={20}
                  strokeWidth={1.5}
                  className="text-[#1E293B] flex-shrink-0"
                />
                <p className="font-sans text-base text-[#2A2724]">{benefit}</p>
              </div>
            </FadeUp>
          ))}
        </div>

        <FadeUp delay={620}>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-10">
            <span className="font-sans text-sm text-[#57534E] flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#1E293B] inline-block" />
              Sans engagement
            </span>
            <span className="font-sans text-sm text-[#57534E] flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#1E293B] inline-block" />
              Sans pression
            </span>
          </div>
        </FadeUp>

        <FadeUp delay={720}>
          <p
            className="font-serif text-xl sm:text-2xl text-[#2A2724] italic leading-relaxed mb-10"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Cet appel peut déjà changer ta façon de comprendre ce que tu vis aujourd'hui.
          </p>
        </FadeUp>

        <FadeUp delay={800}>
          <CTAButton size="large" />
        </FadeUp>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────
// SECTION — CTA INTERMÉDIAIRE (après appel)
// ─────────────────────────────────────────
const MidCTASection = () => (
  <section data-testid="mid-cta-section" className="py-16 bg-[#FDFBF7]">
    <div className="max-w-2xl mx-auto px-6 text-center">
      <FadeUp>
        <CTAButton size="large" />
      </FadeUp>
    </div>
  </section>
);

// ─────────────────────────────────────────
// SECTION 6 — POUR QUI / POUR QUI PAS
// ─────────────────────────────────────────
const PourQuiSection = () => {
  const pourQui = [
    "tu te sens épuisée depuis trop longtemps, sans vraie raison apparente",
    "tu as l'impression de tout porter et de ne jamais pouvoir souffler",
    "tu veux comprendre pourquoi tu restes bloquée — et en sortir",
  ];
  const pasQui = [
    "tu cherches juste de la curiosité, sans intention de changer",
    "tu n'es pas prête à regarder ce qui se passe vraiment",
    "tu attends une solution magique ou instantanée",
  ];

  return (
    <section
      data-testid="pour-qui-section"
      className="py-24 lg:py-32 bg-[#FDFBF7]"
    >
      <div className="max-w-5xl mx-auto px-6">
        <FadeUp className="text-center mb-16">
          <p className="font-sans uppercase tracking-[0.25em] text-xs text-[#57534E] mb-4">
            Es-tu concernée ?
          </p>
          <h2
            className="font-serif text-4xl sm:text-5xl text-[#2A2724]"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Cet appel est fait pour vous
          </h2>
        </FadeUp>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Pour qui */}
          <FadeUp delay={120}>
            <div className="bg-[#F5F2EA] rounded-3xl p-8 h-full border border-[#2A2724]/5">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 rounded-2xl bg-[#1E293B] flex items-center justify-center flex-shrink-0">
                  <CheckCircle size={18} strokeWidth={1.5} className="text-white" />
                </div>
                <h3
                  className="font-serif text-2xl text-[#2A2724]"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  C'est pour toi si…
                </h3>
              </div>
              <div className="space-y-4">
                {pourQui.map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle
                      size={15}
                      strokeWidth={2}
                      className="text-[#1E293B] mt-1 flex-shrink-0"
                    />
                    <p className="font-sans text-sm text-[#57534E] leading-relaxed">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </FadeUp>

          {/* Pas pour qui */}
          <FadeUp delay={240}>
            <div className="bg-[#2A2724]/4 rounded-3xl p-8 h-full border border-[#2A2724]/8">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 rounded-2xl bg-[#57534E]/20 flex items-center justify-center flex-shrink-0">
                  <XCircle size={18} strokeWidth={1.5} className="text-[#57534E]" />
                </div>
                <h3
                  className="font-serif text-2xl text-[#2A2724]"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  Ce n'est pas pour toi si…
                </h3>
              </div>
              <div className="space-y-4">
                {pasQui.map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <XCircle
                      size={15}
                      strokeWidth={2}
                      className="text-[#57534E] mt-1 flex-shrink-0"
                    />
                    <p className="font-sans text-sm text-[#57534E] leading-relaxed">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────
// SECTION 7 — RÉASSURANCE (Bruno)
// ─────────────────────────────────────────
const ReassuanceSection = () => {
  const signals = [
    {
      icon: Lock,
      title: "Appel confidentiel",
      desc: "Tout reste entre nous. Zéro jugement.",
    },
    {
      icon: MessageCircle,
      title: "Un vrai échange humain",
      desc: "Pas un script. Pas un discours commercial. Juste une conversation honnête.",
    },
    {
      icon: Lightbulb,
      title: "Tu repars avec de la clarté",
      desc: "Même sans engagement, tu comprendras mieux ce que tu vis.",
    },
  ];

  return (
    <section
      data-testid="reassurance-section"
      className="py-24 lg:py-32 bg-[#F5F2EA]"
    >
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Photo placeholder Bruno */}
          <FadeUp>
            <div className="relative">
              <div className="overflow-hidden rounded-3xl aspect-[3/4] max-w-sm mx-auto shadow-sm">
                <img
                  src="/bruno.jpg"
                  alt="Bruno Carvalho — Noctivue"
                  className="w-full h-full object-cover img-hover"
                  data-testid="coach-photo-placeholder"
                />
              </div>
              <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 lg:left-auto lg:translate-x-0 lg:-right-6 bg-[#1E293B] text-white rounded-2xl px-6 py-4 shadow-lg w-max">
                <p className="font-sans text-[10px] text-[#D1C7BD] uppercase tracking-widest mb-0.5">
                  Coach
                </p>
                <p
                  className="font-serif text-lg text-white"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  Bruno Carvalho
                </p>
                <p className="font-sans text-[10px] text-[#D1C7BD] tracking-widest">
                  Noctivue
                </p>
              </div>
            </div>
          </FadeUp>

          {/* Contenu */}
          <div className="space-y-10 mt-8 lg:mt-0">
            <FadeUp delay={100}>
              <p className="font-sans uppercase tracking-[0.25em] text-xs text-[#57534E]">
                Ce que tu peux attendre
              </p>
            </FadeUp>
            <FadeUp delay={180}>
              <h2
                className="font-serif text-4xl sm:text-5xl text-[#2A2724] leading-tight"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Un espace sécurisé
                <br />
                pour que <em>ton corps sorte du mode survie</em>
              </h2>
            </FadeUp>

            <div className="space-y-6">
              {signals.map((signal, i) => (
                <FadeUp key={i} delay={280 + i * 110}>
                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 rounded-2xl bg-[#1E293B] flex items-center justify-center flex-shrink-0">
                      <signal.icon
                        size={20}
                        strokeWidth={1.5}
                        className="text-white"
                      />
                    </div>
                    <div>
                      <h3 className="font-sans font-semibold text-[#2A2724] text-base mb-1">
                        {signal.title}
                      </h3>
                      <p className="font-sans text-sm text-[#57534E] leading-relaxed">
                        {signal.desc}
                      </p>
                    </div>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────
// SECTION — PREUVE (Témoignages)
// ─────────────────────────────────────────
const PreuveSection = () => {
  const temoignages = [
    "Pendant des mois, je fonctionnais en automatique. Je portais tout. Je ne me rendais même plus compte que j'étais épuisée. Aujourd'hui, je sens que quelque chose a lâché à l'intérieur.",
    "Je croyais que c'était normal de vivre comme ça. Toujours en alerte, toujours à anticiper. Maintenant je comprends que mon corps était coincé en mode survie depuis des années.",
    "Pour la première fois, je me suis réveillée sans cette boule au ventre. Pas parce que j'ai compris quelque chose. Parce que mon corps a enfin pu relâcher.",
  ];

  return (
    <section data-testid="preuve-section" className="py-24 lg:py-28 bg-[#F5F2EA]">
      <div className="max-w-3xl mx-auto px-6 text-center">
        <FadeUp>
          <p className="font-sans uppercase tracking-[0.25em] text-xs text-[#57534E] mb-6">
            Témoignages
          </p>
        </FadeUp>

        <FadeUp delay={100}>
          <h2
            className="font-serif text-3xl sm:text-4xl text-[#2A2724] leading-tight mb-14"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Ce que certaines femmes ressentent
            <br />
            <em>après cet accompagnement</em>
          </h2>
        </FadeUp>

        <div className="space-y-6">
          {temoignages.map((t, i) => (
            <FadeUp key={i} delay={200 + i * 120}>
              <div className="bg-white/70 border border-[#2A2724]/6 rounded-2xl px-8 py-7">
                <p
                  className="font-serif text-lg sm:text-xl text-[#2A2724] italic leading-relaxed"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  "{t}"
                </p>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────
// SECTION — PHRASE FINALE
// ─────────────────────────────────────────
const PhraseFinaleSection = () => (
  <section data-testid="phrase-finale-section" className="py-20 lg:py-24 bg-[#FDFBF7]">
    <div className="max-w-2xl mx-auto px-6 text-center">
      <FadeUp>
        <p
          className="font-serif text-2xl sm:text-3xl text-[#2A2724] italic leading-relaxed"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          Ce que tu vis aujourd'hui n'est pas une faiblesse.
        </p>
      </FadeUp>
      <FadeUp delay={150}>
        <p
          className="font-serif text-xl sm:text-2xl text-[#57534E] italic leading-relaxed mt-4"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          C'est ton corps qui fonctionne en mode survie depuis trop longtemps. Et il peut en sortir.
        </p>
      </FadeUp>
    </div>
  </section>
);

// ─────────────────────────────────────────
// SECTION 8 — CTA FINAL (Calendly)
// ─────────────────────────────────────────
const CTAFinalSection = () => (
  <section
    data-testid="cta-final-section"
    className="py-32 bg-[#1E293B] relative overflow-hidden"
  >
    <div
      className="absolute inset-0 bg-cover bg-center pointer-events-none"
      style={{
        backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/625f59e0-0a35-409d-822e-917cb1e67548/images/80aed8ddfeadc76fd3511aaf0dd96b320378f4d569309965d0e7faf705ec7a58.png')`,
        opacity: 0.05,
      }}
    />
    <div className="relative z-10 max-w-2xl mx-auto px-6 text-center">
      <FadeUp>
        <p className="font-sans uppercase tracking-[0.3em] text-xs text-[#D1C7BD] mb-8">
          Première étape
        </p>
      </FadeUp>
      <FadeUp delay={100}>
        <h2
          className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white leading-tight mb-8"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          Tu es prête
          <br />
          <em className="text-[#D1C7BD]">à sortir du mode survie ?</em>
        </h2>
      </FadeUp>
      <FadeUp delay={200}>
        <p className="font-sans text-base text-[#D1C7BD]/80 leading-relaxed mb-4">
          Tu as assez porté. Cet appel est gratuit, sans engagement et sans pression.
        </p>
      </FadeUp>
      <FadeUp delay={280}>
        <CTAButton size="large" dark={false} />
      </FadeUp>
    </div>
  </section>
);

// ─────────────────────────────────────────
// SECTION — CTA HAUT DE PAGE (après accroche)
// ─────────────────────────────────────────
const TopCTASection = () => (
  <section data-testid="top-cta-section" className="py-12 bg-[#FDFBF7]">
    <div className="max-w-2xl mx-auto px-6 text-center">
      <FadeUp>
        <CTAButton size="large" />
      </FadeUp>
    </div>
  </section>
);

// ─────────────────────────────────────────
// FOOTER
// ─────────────────────────────────────────
const Footer = () => (
  <footer className="py-8 bg-[#0F172A] text-center">
    <p className="font-sans text-xs text-[#D1C7BD]/40 tracking-[0.2em] uppercase">
      © 2025 Noctivue — Bruno Carvalho
    </p>
  </footer>
);

// ─────────────────────────────────────────
// APPLICATION PRINCIPALE
// ─────────────────────────────────────────
function App() {
  return (
    <div
      className="App"
      style={{ fontFamily: "'Manrope', sans-serif" }}
    >
      <Header />
      {/* 1. Accroche + CTA intégré */}
      <HeroSection />
      {/* 3. Identification */}
      <IdentificationSection />
      {/* 4. Problème */}
      <ProblemeSection />
      {/* 5. Urgence */}
      <UrgenceSection />
      {/* 6. Solution */}
      <SolutionSection />
      {/* 7. Projection */}
      <ProjectionSection />
      {/* 8. Différenciation */}
      <DifferencationSection />
      {/* 9. Appel */}
      <AppelDecouverteSection />
      {/* 10. Filtrage */}
      <PourQuiSection />
      {/* 12. Preuve */}
      <PreuveSection />
      {/* 13. Phrase finale */}
      <PhraseFinaleSection />
      {/* 14. CTA final */}
      <CTAFinalSection />
      <Footer />
    </div>
  );
}

export default App;
